import { BoardID } from 'BoardID';
import {
  changePatternMaterial,
  changePatternSize,
  createRandomMaterialPatternString,
  MaterialPattern,
  MaterialPatternString,
  parseMaterialPatternString,
  parsePatternString,
  scalePatternString,
  sumPattern,
} from 'Pattern';
import {
  BoardDetails,
  BoardType,
  Dimensions2D,
  Direction,
  Material,
  ParamsPanelSelection,
  Renderable,
  RenderableMetadata,
  Settings,
  TwoArray,
  Units,
  WeaveParams,
} from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import {
  fillRenderablesAt,
  groupBy,
  mapMapEntries,
  randomInt,
  scaleNumber,
  sumBy,
} from 'utils/utils';

const BACKGROUND_SELECTION_GROUP = 'background';

export default class Weave extends BoardBase<BoardType.Weave> {
  constructor(id: BoardID, name: string, params: WeaveParams, units?: Units) {
    super(BoardType.Weave, id, name, params, units);
  }

  static createRandom(
    materials: TwoArray<Material>,
    size: Dimensions2D
  ): WeaveParams {
    const horizontalSize = randomInt(2, 4);
    const backgroundSize = randomInt(4, 12) / 8;
    const totalBackgroundSize = (horizontalSize + 1) * backgroundSize;
    const patternSize = (size.width - totalBackgroundSize) / horizontalSize;

    return {
      horizontalSize,
      verticalSize: Math.ceil(16 / (backgroundSize + patternSize)),
      pattern: createRandomMaterialPatternString(
        1 / 4,
        2,
        patternSize,
        materials
      ),
      thickness: 1,
      background: `${backgroundSize}C` as MaterialPatternString,
    };
  }

  buildStepsComponent = null;

  getPattern(): MaterialPattern {
    return parseMaterialPatternString(this.params.pattern);
  }

  getDimensions(): Dimensions2D {
    const { background, horizontalSize, pattern, verticalSize } = this.params;
    const parsedPattern = parsePatternString(pattern);
    const patternSum = sumPattern(parsedPattern);
    const backgroundSize = parsePatternString(background)[0].size;

    return {
      width:
        horizontalSize * patternSum + backgroundSize * (horizontalSize + 1),
      height: verticalSize * patternSum + backgroundSize * (verticalSize + 1),
    };
  }

  getRenderables(): Array<Renderable<BoardType.Weave>> {
    const { horizontalSize, verticalSize } = this.params;
    const background = parsePatternString(this.params.background)[0];
    const pattern = parsePatternString(this.params.pattern);
    const patternSize = sumPattern(pattern);
    const dimensions = this.getDimensions();

    const renderables: Array<Renderable<BoardType.Weave>> = [];

    // Background blocks
    for (let rowIndex = 0; rowIndex <= verticalSize; ++rowIndex) {
      for (let colIndex = 0; colIndex <= horizontalSize; ++colIndex) {
        renderables.push({
          material: background.key,
          rect: {
            top: rowIndex * (patternSize + background.size),
            left: colIndex * (patternSize + background.size),
            height: background.size,
            width: background.size,
          },
          metadata: {
            fragmentIndex: BACKGROUND_SELECTION_GROUP,
            renderableIndex: renderables.length,
          },
          selectionSizeProp: 'width',
        });
      }
    }

    // Horizontal strips
    for (let rowIndex = 0; rowIndex < verticalSize; ++rowIndex) {
      weaveThread(
        renderables,
        pattern,
        patternSize,
        background.size,
        rowIndex % 2 === 1,
        background.size + rowIndex * (patternSize + background.size),
        dimensions.width,
        Direction.V
      );
    }

    // Vertical strips
    for (let colIndex = 0; colIndex < horizontalSize; ++colIndex) {
      weaveThread(
        renderables,
        pattern,
        patternSize,
        background.size,
        colIndex % 2 === 0,
        background.size + colIndex * (patternSize + background.size),
        dimensions.height,
        Direction.H
      );
    }

    return renderables;
  }

  getIsRenderableHighlighted(
    renderable: Renderable<BoardType.Weave>,
    selection: Renderable<BoardType.Weave>
  ): boolean {
    return (
      renderable.metadata.fragmentIndex === selection.metadata.fragmentIndex
    );
  }

  getBoardDetails(settings: Settings): BoardDetails {
    const { kerf, planeBuffer } = settings;
    const { horizontalSize, verticalSize, thickness } = this.params;
    const background = parsePatternString(this.params.background)[0];
    const pattern = parsePatternString(this.params.pattern);
    const patternFragmentsByMaterial = groupBy(pattern, 'key');

    const numBgBlocks = (verticalSize + 1) * (horizontalSize + 1);
    const startingBgStockLength =
      numBgBlocks * (thickness + planeBuffer) + (numBgBlocks - 1 * kerf);

    const numShortLengths = verticalSize + horizontalSize;
    const numLongLengths = verticalSize * horizontalSize;
    const numLengths = numShortLengths + numLongLengths;
    const startingStockLength =
      numShortLengths * background.size +
      numLongLengths * background.size * 3 +
      (numLengths - 1) * kerf;

    const startingStockThickness = background.size + planeBuffer;

    const startingStockByMaterial = mapMapEntries(
      patternFragmentsByMaterial,
      (material, dimensions) => ({
        material,
        dimensions: {
          length: startingStockLength,
          thickness: startingStockThickness,
          width:
            sumBy(dimensions, ({ size }) => size + planeBuffer) +
            (dimensions.length - 1) * kerf,
        },
      })
    );

    return {
      idealStartingStock: [
        {
          label: 'Background',
          stock:
            background.key == null
              ? []
              : [
                  {
                    material: background.key,
                    dimensions: {
                      length: startingBgStockLength,
                      width: background.size + planeBuffer,
                      thickness: background.size + planeBuffer,
                    },
                  },
                ],
        },
        { label: 'Weave', stock: startingStockByMaterial },
      ],
    };
  }

  handleSelectedSizeChange(
    { metadata }: Renderable<BoardType.Weave>,
    size: number
  ) {
    if (metadata.fragmentIndex === BACKGROUND_SELECTION_GROUP) {
      return this.updateParams({
        background: changePatternSize(this.params.background, 0, size),
      });
    }

    return this.updateParams({
      pattern: changePatternSize(
        this.params.pattern,
        metadata.fragmentIndex,
        size
      ),
    });
  }

  handleSelectedMaterialChange(
    { metadata }: Renderable<BoardType.Weave>,
    material: Material
  ) {
    if (metadata.fragmentIndex === BACKGROUND_SELECTION_GROUP) {
      return this.updateParams({
        background: changePatternMaterial(this.params.background, 0, material),
      });
    }

    return this.updateParams({
      pattern: changePatternMaterial(
        this.params.pattern,
        +metadata.fragmentIndex,
        material
      ),
    });
  }

  previewSelectionToParamsPanelSelection(
    selection: RenderableMetadata<BoardType.Weave>
  ): null | ParamsPanelSelection<BoardType.Chevron> {
    return selection.fragmentIndex === 'background'
      ? null
      : {
          renderableIndex: selection.fragmentIndex,
        };
  }

  paramsPanelSelectionToPreviewSelection(
    selection: ParamsPanelSelection<BoardType.Chevron>
  ): null | RenderableMetadata<BoardType.Weave> {
    return selection == null
      ? null
      : {
          fragmentIndex: selection.renderableIndex,
          renderableIndex: selection.renderableIndex,
        };
  }

  scaleParams(params: WeaveParams, scaleFactor: number): WeaveParams {
    return {
      ...params,
      thickness: scaleNumber(params.thickness, scaleFactor),
      background: scalePatternString(params.background, scaleFactor),
      pattern: scalePatternString(params.pattern, scaleFactor),
    };
  }
}

function weaveThread(
  renderables: Array<Renderable<BoardType.Weave>>,
  pattern: MaterialPattern,
  patternSize: number,
  backgroundSize: number,
  startUnder: boolean,
  offset: number,
  totalSize: number,
  direction: Direction
) {
  const longSize = backgroundSize + patternSize + backgroundSize;
  const startingThreadSize = startUnder ? backgroundSize : longSize;

  let threadOffset = 0;

  fillRenderablesAt(
    renderables,
    pattern,
    direction === Direction.V ? offset : threadOffset,
    direction === Direction.V ? threadOffset : offset,
    startingThreadSize,
    direction,
    ({ fragmentIndex, renderableIndex }) => ({
      type: BoardType.Weave,
      fragmentIndex,
      renderableIndex,
    })
  );
  threadOffset += startingThreadSize + patternSize;

  while (threadOffset < totalSize) {
    fillRenderablesAt(
      renderables,
      pattern,
      direction === Direction.V ? offset : threadOffset,
      direction === Direction.V ? threadOffset : offset,
      longSize,
      direction,
      ({ fragmentIndex, renderableIndex }) => ({
        type: BoardType.Weave,
        fragmentIndex,
        renderableIndex,
      })
    );
    threadOffset += longSize + patternSize;
  }

  if (threadOffset < totalSize) {
    fillRenderablesAt(
      renderables,
      pattern,
      direction === Direction.V ? offset : threadOffset,
      direction === Direction.V ? threadOffset : offset,
      totalSize - threadOffset,
      Direction.V,
      ({ fragmentIndex, renderableIndex }) => ({
        type: BoardType.Weave,
        fragmentIndex,
        renderableIndex,
      })
    );
  }
}
